/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { getReEnterPropertyId } from 'reEnter';
import React, { Fragment, useCallback, useState } from 'react';
import {
  FieldWrapper,
  FormPageProperty,
  MAIL_REFUSAL_FLAG_PROPERTY_INPUT_TYPE,
  MULTIPLE_SELECTION_PROPERTY_INPUT_TYPE,
  PasswordHashProperty,
  PROPERTY_TYPE,
  ReEnterInputWrapper,
  SINGLE_SELECTION_PROPERTY_INPUT_TYPE,
} from 'shared';
import { DateItem } from './Date';
import { FileUpload } from './FileUpload';
import { MailAddress } from './MailAddress';
import { MailRefusalFlag } from './MailRefusalFlag';
import { MonthDay } from './MonthDay';
import { MultipleSelection } from './MultipleSelection';
import { NumberItem } from './Number';
import { PasswordHash } from './PasswordHash';
import { SingleSelection } from './SingleSelection';
import { Text } from './Text';
import { FormFieldProps } from './shared';

const reEnterStyle = css`
  margin-top: 8px;
`;

const PasswordHashContainer: React.FC<FormFieldProps<PasswordHashProperty>> = ({
  property,
  disabled,
  design,
  submitted,
}) => {
  const [displayPassword, setDisplayPassword] = useState(false);
  const handlePasswordDisplayChange = useCallback(() => {
    setDisplayPassword((prev) => !prev);
  }, []);
  const { reEnter } = property;
  if (!reEnter) {
    return (
      <PasswordHash
        property={property}
        disabled={disabled}
        design={design}
        submitted={submitted}
        displayPassword={displayPassword}
        onPasswordDisplayChange={handlePasswordDisplayChange}
      />
    );
  }

  const reEnterProperty = {
    ...property,
    propertyId: getReEnterPropertyId(property.propertyId),
  };
  return (
    <Fragment>
      <PasswordHash
        property={property}
        disabled={disabled}
        design={design}
        submitted={submitted}
        displayPassword={displayPassword}
        onPasswordDisplayChange={handlePasswordDisplayChange}
      />
      <ReEnterInputWrapper
        color={design.colors.propertyTitleFont}
        css={reEnterStyle}
        name={reEnter.title}
        reEnterPropertyId={reEnterProperty.propertyId}
      >
        <PasswordHash
          property={reEnterProperty}
          disabled={disabled}
          design={design}
          submitted={submitted}
          displayPassword={displayPassword}
          onPasswordDisplayChange={handlePasswordDisplayChange}
          reEnter={true}
        />
      </ReEnterInputWrapper>
    </Fragment>
  );
};

const FormFieldInput: React.FC<FormFieldProps> = ({
  property,
  disabled,
  design,
  submitted,
}) => {
  const {
    mailAddress,
    text,
    number,
    date,
    monthDay,
    singleSelection,
    multipleSelection,
    mailRefusalFlag,
    passwordHash,
    fileUpload,
  } = PROPERTY_TYPE;
  const baseProps = {
    property,
    disabled,
    design,
    submitted,
  };

  switch (property.propertyType) {
    case mailAddress: {
      if (!property.reEnter) {
        return <MailAddress {...baseProps} property={property} />;
      }
      const reEnterProperty = {
        ...property,
        propertyId: getReEnterPropertyId(property.propertyId),
      };
      return (
        <Fragment>
          <MailAddress {...baseProps} property={property} />
          <ReEnterInputWrapper
            color={design.colors.propertyTitleFont}
            css={reEnterStyle}
            name={property.reEnter.title}
            reEnterPropertyId={reEnterProperty.propertyId}
          >
            <MailAddress
              {...baseProps}
              property={reEnterProperty}
              reEnter={true}
            />
          </ReEnterInputWrapper>
        </Fragment>
      );
    }
    case text: {
      if (!property.reEnter) {
        return <Text {...baseProps} property={property} />;
      }
      const reEnterProperty = {
        ...property,
        propertyId: getReEnterPropertyId(property.propertyId),
      };
      return (
        <Fragment>
          <Text {...baseProps} property={property} />
          <ReEnterInputWrapper
            color={design.colors.propertyTitleFont}
            css={reEnterStyle}
            name={property.reEnter.title}
          >
            <Text
              {...baseProps}
              property={reEnterProperty}
              css={reEnterStyle}
              reEnter={true}
            />
          </ReEnterInputWrapper>
        </Fragment>
      );
    }
    case number: {
      return <NumberItem {...baseProps} property={property} />;
    }
    case date: {
      return <DateItem {...baseProps} property={property} />;
    }
    case monthDay: {
      return <MonthDay {...baseProps} property={property} />;
    }
    case singleSelection: {
      return <SingleSelection {...baseProps} property={property} />;
    }
    case multipleSelection: {
      return <MultipleSelection {...baseProps} property={property} />;
    }
    case mailRefusalFlag: {
      return <MailRefusalFlag {...baseProps} property={property} />;
    }
    case passwordHash: {
      return <PasswordHashContainer {...baseProps} property={property} />;
    }
    case fileUpload: {
      return <FileUpload {...baseProps} property={property} />;
    }
  }
};

const shouldNotUseLabelTag = (property: FormPageProperty): boolean => {
  if (
    property.propertyType === PROPERTY_TYPE.singleSelection &&
    property.inputFormat.inputType ===
      SINGLE_SELECTION_PROPERTY_INPUT_TYPE.radio
  ) {
    return true;
  }
  if (
    property.propertyType === PROPERTY_TYPE.multipleSelection &&
    property.inputFormat.inputType ===
      MULTIPLE_SELECTION_PROPERTY_INPUT_TYPE.checkbox
  ) {
    return true;
  }
  if (
    property.propertyType === PROPERTY_TYPE.mailRefusalFlag &&
    property.inputFormat.inputType ===
      MAIL_REFUSAL_FLAG_PROPERTY_INPUT_TYPE.radio
  ) {
    return true;
  }
  return false;
};

export const FormField: React.FC<
  FormFieldProps & { required: boolean; className?: string }
> = ({ property, required, disabled, design, className, submitted }) => {
  const { name, note, comment, displayRequiredLabel } = property;
  const fieldWrapperProps = {
    displayRequiredLabel,
    required,
    name,
    note: note ?? '',
    comment: comment ?? '',
    design,
    propertyId: property.propertyId,
  };
  const formFieldProps = {
    property,
    disabled,
    design,
    submitted,
  };
  return (
    <FieldWrapper
      className={className}
      notUseLabelTag={shouldNotUseLabelTag(property)}
      isFileUploadItem={property.propertyType === PROPERTY_TYPE.fileUpload}
      {...fieldWrapperProps}
    >
      <FormFieldInput {...formFieldProps} />
    </FieldWrapper>
  );
};
